import { StashPrimaryColorGroup } from '@leaflink/stash';
import { IconName } from '@leaflink/stash/utils/Icon.vue';
import { RouteLocation, RouteLocationRaw } from 'vue-router';

import { ROUTE_NAMES } from '@/constants/routeNames';
import { Campaign } from '@/models/campaign';
import { BadgeCount, Company } from '@/models/company';
import { SplitName, SplitValue } from '@/models/split';
import { UserCompany } from '@/models/userCompany';
import { useShopStore } from '@/piniaStores/shop/shop.store';
import { InternalUserState } from '@/piniaStores/user/user.state';
import { getWarehouseRouteNameFromRedirects } from '@/router/warehouse';
import { canStaffAccessManageLicenses } from '@/utils/routeAuthz';

function pathStartsWith({
  path,
  value,
  prependSlug = false,
}: {
  path: string;
  value: string;
  prependSlug?: boolean;
}): boolean {
  const withSlug = prependSlug ? '/c/[^/]+' : '';
  const regex = new RegExp(`^${withSlug}/${value}(([/].*)+)?$`);

  return !!path.match(regex);
}

export interface MenuState {
  user: InternalUserState;
  activeCampaign?: Campaign;
  activeCompany?: Company;
  isSplitEnabled: (splitName: SplitName, allowedValue?: SplitValue | SplitValue[]) => boolean;
  userCompany?: UserCompany;
  currentRoute: RouteLocation;
  badgeCounts: BadgeCount;
}

export interface AppNavigationBadge {
  count: number;
  color?: StashPrimaryColorGroup;
}
export interface AppNavigationConfigItem {
  id: string;
  label: ((state: MenuState) => string) | string;
  icon: IconName;
  visible: ((state: MenuState) => boolean) | boolean;
  route?: ((state: MenuState) => RouteLocationRaw) | RouteLocationRaw;
  href?: ((state: MenuState) => string) | string;
  active?: ((state: MenuState) => boolean) | boolean;
  children?: ((state: MenuState) => AppNavigationConfigItem[]) | AppNavigationConfigItem[];
  badge?: ((state: MenuState) => AppNavigationBadge) | AppNavigationBadge;
  warning?: ((state: MenuState) => boolean) | boolean;
}

export interface ResolvedAppNavigationConfigItem
  extends Omit<AppNavigationConfigItem, 'badge' | 'href' | 'label' | 'route'> {
  route?: RouteLocationRaw;
  label: string;
  href?: string;
  badge?: AppNavigationBadge;
}

export type NavConfig = (AppNavigationConfigItem | ((menuState: MenuState) => ResolvedAppNavigationConfigItem))[];

/**
 * `active` prop:
 *
 * Use the `active` prop when custom rules, or more specific targetting needs to apply
 * Eg. there are multiple nav items that start with 'shop/', so we want to avoid
 * highlighting all of them.
 *
 * Otherwise, AppNavigationItem will handle the active state when the link and destination path match,
 * or when the destination path starts with the link path.
 * It's possible for the destination to differ from the link path when a redirect is defined.
 */
const navConfig: NavConfig = [
  {
    id: 'admin-tools',
    label: 'layout.sideNav.adminTools',
    icon: 'tool-wrench',
    route: { name: ROUTE_NAMES.ADMIN_TOOLS },
    visible: ({ user }) => user.isLeaflinkStaff && user.isSuperuser,
  },
  {
    id: 'company-dashboard',
    label: 'layout.sideNav.dashboard',
    icon: 'dashboard',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.COMPANY_DASHBOARD,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ user, activeCompany }) =>
      !!(user.isBuyer && activeCompany?.buyer && !user.onlySmp && !activeCompany?.isLogisticsOnly),
    active: ({ currentRoute }) => currentRoute.name === ROUTE_NAMES.COMPANY_DASHBOARD,
  },
  {
    id: 'buy-it-again',
    label: 'layout.sideNav.buyItAgain',
    icon: 'shop-bag-reorder',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.PURCHASE_HISTORY_NEW,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) => {
      return !!(activeCompany?.buyer && userCompany?.staffPermissions?.canPurchase && !activeCompany?.isLogisticsOnly);
    },
    active: ({ currentRoute }) => {
      return [ROUTE_NAMES.PURCHASE_HISTORY_NEW, ROUTE_NAMES.STORE_FAVORITES].includes(String(currentRoute.name));
    },
  },
  {
    id: 'shop',
    label: 'layout.sideNav.shop',
    icon: 'shop-bag',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.SHOP,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) => {
      return Boolean(
        activeCompany?.buyer && userCompany?.staffPermissions.canPurchase && !activeCompany?.isLogisticsOnly,
      );
    },
    active: ({ currentRoute }) => {
      return [
        ROUTE_NAMES.BRAND_MENU,
        ROUTE_NAMES.SHOP_BRANDS,
        ROUTE_NAMES.SHOP_PRODUCTS,
        ROUTE_NAMES.DISCOVER_PRODUCTS,
      ].includes(String(currentRoute.name));
    },
  },
  {
    id: 'replenishment-catalog',
    label: 'layout.sideNav.catalog',
    icon: 'product-menu',
    route: () => ({
      name: ROUTE_NAMES.REPLENISHMENT_CATALOG,
    }),
    visible: ({ activeCompany, isSplitEnabled, userCompany }) => {
      return !!(
        isSplitEnabled('replenishment_catalog_page') &&
        activeCompany?.group &&
        activeCompany?.group.enableReplenishment &&
        userCompany?.staffPermissions.canAccessReplenishment
      );
    },
    active: ({ currentRoute }) => currentRoute.name === ROUTE_NAMES.REPLENISHMENT_CATALOG,
  },
  {
    id: 'shop-deals',
    label: ({ activeCampaign }) => activeCampaign?.label || '',
    icon: 'tag-star',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.SHOP_DEALS,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) => {
      const shopStore = useShopStore();

      return !!(
        shopStore.getIsCampaignLive &&
        activeCompany?.buyer &&
        userCompany?.staffPermissions.canPurchase &&
        !activeCompany?.isLogisticsOnly
      );
    },
    active: ({ currentRoute }) => currentRoute.name === ROUTE_NAMES.SHOP_DEALS,
  },
  {
    id: 'customers',
    label: 'layout.sideNav.customers',
    icon: 'book-customer',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.CUSTOMERS,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) =>
      !!(activeCompany?.seller && userCompany?.staffPermissions.canManageCrm && !activeCompany?.isLogisticsOnly),
    active: ({ currentRoute }) =>
      pathStartsWith({
        path: currentRoute.path,
        value: ROUTE_NAMES.CUSTOMERS,
        prependSlug: true,
      }) ||
      currentRoute.name === ROUTE_NAMES.SERVICE_ZONES ||
      currentRoute.name === ROUTE_NAMES.CUSTOMER_STATUSES,
  },
  {
    id: 'preview-menu-top',
    label: 'layout.sideNav.previewMenu',
    icon: 'preview',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.SHOP_BRAND_PREVIEW,
      params: {
        company: activeCompany?.slug,
        brandId: activeCompany?.brandsForPreview?.[0]?.id.toString(),
      },
    }),
    visible: ({ activeCompany }) => {
      return !!(
        (activeCompany?.seller || activeCompany?.isBrandOwner) &&
        activeCompany?.brandsForPreview.length === 1 &&
        !activeCompany?.isLogisticsOnly
      );
    },
    active: ({ currentRoute }) => currentRoute.name === ROUTE_NAMES.SHOP_BRAND_PREVIEW,
  },
  {
    id: 'preview-menu-children-wrapper',
    label: 'layout.sideNav.previewMenu',
    icon: 'preview',
    route: undefined,
    visible: ({ activeCompany }) => {
      return !!(
        (activeCompany?.seller || activeCompany?.isBrandOwner) &&
        activeCompany?.brandsForPreview.length > 1 &&
        !activeCompany?.isLogisticsOnly
      );
    },
    active: false,
    children: ({ activeCompany, currentRoute }) => {
      return (activeCompany?.brandsForPreview || []).map((brand) => ({
        id: `preview-menu-child-${brand.id}`,
        label: brand.name,
        icon: 'building-office',
        route: {
          name: ROUTE_NAMES.SHOP_BRAND_PREVIEW,
          params: {
            company: activeCompany?.slug,
            brandId: brand.id.toString(),
          },
        },
        visible: true,
        active:
          currentRoute.name === ROUTE_NAMES.SHOP_BRAND_PREVIEW && currentRoute.params.brandId === brand.id.toString(),
      }));
    },
  },
  {
    id: 'advertising',
    label: 'layout.sideNav.advertising',
    icon: 'megaphone-sound',
    route: () => ({
      name: ROUTE_NAMES.ADVERTISING,
    }),
    visible: ({ activeCompany, isSplitEnabled }) =>
      !!(activeCompany?.seller && isSplitEnabled('ads_in_platform_product_promotion')),
    active: ({ currentRoute }) =>
      pathStartsWith({
        path: currentRoute.path,
        value: ROUTE_NAMES.ADVERTISING,
        prependSlug: false,
      }),
  },
  {
    id: 'inventory',
    label: 'layout.sideNav.inventory',
    icon: 'clipboard-inventory',
    route: ({ activeCompany, isSplitEnabled }) => ({
      name: isSplitEnabled('inventory_pod__new_product_list_page') ? ROUTE_NAMES.PRODUCTS_NEW : ROUTE_NAMES.PRODUCTS,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) =>
      !!(activeCompany?.seller && userCompany?.staffPermissions.canManageInventory && !activeCompany?.isLogisticsOnly),
    active: ({ currentRoute }) =>
      pathStartsWith({
        path: currentRoute.path,
        value: 'inventory',
        prependSlug: true,
      }),
  },
  {
    id: 'seller-seed-to-sale',
    label: 'layout.sideNav.seedToSale',
    icon: 'seed-cycle',
    route: ({ activeCompany, user }) => ({
      name: user.permissions.seedToSale.canAccessSeedToSalePackages
        ? ROUTE_NAMES.SEED_TO_SALE_PACKAGES
        : ROUTE_NAMES.SEED_TO_SALE_REFRESH_REQUESTS,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, user }) =>
      !!(activeCompany?.seller && user.permissions.seedToSale.canAccessSeedToSalePage),
    active: ({ currentRoute, user }) =>
      user.permissions.seedToSale.canAccessSeedToSalePackages
        ? currentRoute.name === ROUTE_NAMES.SEED_TO_SALE_PACKAGES
        : currentRoute.name === ROUTE_NAMES.SEED_TO_SALE_REFRESH_REQUESTS,
  },
  {
    id: 'Messages',
    label: 'layout.sideNav.messages',
    icon: 'message',
    route: ({ activeCompany, isSplitEnabled }) => ({
      name: isSplitEnabled('vue_messages_page') ? ROUTE_NAMES.MESSAGES : ROUTE_NAMES.DISCUSSIONS,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) => {
      if (
        activeCompany?.isDeliveryProvider ||
        activeCompany?.isBrandOwner ||
        !userCompany?.canDiscuss ||
        activeCompany?.isLogisticsOnly
      ) {
        return false;
      }

      if (activeCompany?.seller && !userCompany?.staffPermissions.canManageOrdersReceived) {
        // In this case, the Messages route permissions in router/company.ts will redirect to the Company Dashboard
        return false;
      }

      if (activeCompany?.buyer && !userCompany?.staffPermissions.canRequestSamples) {
        // In this case, the Messages route permissions in router/company.ts will redirect to the Company Dashboard
        return false;
      }

      return true;
    },
    active: ({ currentRoute }) =>
      pathStartsWith({
        path: currentRoute.path,
        value: ROUTE_NAMES.DISCUSSIONS,
        prependSlug: true,
      }) ||
      pathStartsWith({
        path: currentRoute.path,
        value: ROUTE_NAMES.MESSAGES,
        prependSlug: true,
      }),
    badge: ({ activeCompany }) => ({
      count: activeCompany?.unreadMessageCount || 0,
    }),
  },
  {
    id: 'pricing',
    label: 'layout.sideNav.pricing',
    icon: 'tag',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.VOLUME_DISCOUNTS,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) => {
      return !!(
        !activeCompany?.isLogisticsOnly &&
        activeCompany?.seller &&
        userCompany?.staffPermissions.canEditPrices
      );
    },
    active: ({ currentRoute }) =>
      pathStartsWith({
        path: currentRoute.path,
        value: 'pricing',
        prependSlug: true,
      }),
  },
  {
    id: 'seller-reports',
    label: ({ activeCompany }) =>
      activeCompany?.seller && activeCompany?.buyer ? 'layout.sideNav.sellerReports' : 'layout.sideNav.reports',
    icon: 'graph-bar-chart',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.REPORTS,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) =>
      !!(
        activeCompany?.seller &&
        userCompany?.staffPermissions.canAccessSalesReports &&
        !activeCompany?.isLogisticsOnly
      ),
    active: ({ currentRoute }) =>
      pathStartsWith({
        path: currentRoute.path,
        value: 'reports',
        prependSlug: true,
      }),
  },
  {
    id: 'brand-owner-reports',
    label: 'layout.sideNav.reports',
    icon: 'graph-bar-chart',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.COMPANY_BRAND_OWNER_REPORTS,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany }) => !!(activeCompany?.isBrandOwner && !activeCompany?.isLogisticsOnly),
  },
  {
    id: 'seller-orders',
    label: 'layout.sideNav.orders',
    icon: 'document',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.ORDERS_RECEIVED,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) =>
      !!(
        activeCompany?.seller &&
        userCompany?.staffPermissions.canManageOrdersReceived &&
        !activeCompany?.isLogisticsOnly
      ),
    active: ({ currentRoute }) =>
      pathStartsWith({
        path: currentRoute.path,
        value: 'orders',
        prependSlug: true,
      }),
    badge: ({ badgeCounts }) => ({
      count: (badgeCounts.submittedOrders ?? 0) + (badgeCounts.collaborativeOrders ?? 0),
    }),
  },
  {
    id: 'brand-owner-orders',
    label: 'layout.sideNav.orders',
    icon: 'document',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.BRAND_OWNER_ORDERS,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany }) => {
      return !!(
        !activeCompany?.seller &&
        activeCompany?.isBrandOwner &&
        activeCompany?.hasBrandOwnerOrdersEnabled &&
        !activeCompany?.isLogisticsOnly
      );
    },
    active: ({ currentRoute }) =>
      pathStartsWith({
        path: currentRoute.path,
        value: 'brand-owner/orders',
        prependSlug: true,
      }),
    badge: ({ activeCompany }) => ({
      count: activeCompany?.submittedOrdersCount || 0,
    }),
  },
  {
    id: 'buyer-orders',
    label: 'layout.sideNav.orders',
    icon: 'document',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.ORDERS_SENT,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) => {
      return !!(
        !activeCompany?.seller &&
        !activeCompany?.isBrandOwner &&
        activeCompany?.buyer &&
        userCompany?.staffPermissions.canManageOrdersSent &&
        !activeCompany?.isLogisticsOnly
      );
    },
    active: ({ currentRoute }) => {
      return !!(
        currentRoute.name === ROUTE_NAMES.ORDERS_SENT ||
        currentRoute.name === ROUTE_NAMES.COLLABORATIVE_RECEIVED ||
        currentRoute.name === ROUTE_NAMES.INTERNAL_APPROVALS_LIST
      );
    },
    badge: ({ badgeCounts }) => ({
      count: (badgeCounts.submittedOrders ?? 0) + (badgeCounts.collaborativeOrders ?? 0),
      color: badgeCounts.collaborativeOrders ? 'red' : undefined,
    }),
  },
  {
    id: 'buyer-seed-to-sale',
    label: 'layout.sideNav.seedToSale',
    icon: 'seed-cycle',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.SEED_TO_SALE_BUYER_PACKAGES,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, isSplitEnabled, user }) => {
      return !!(
        isSplitEnabled('seed_to_sale__buyer_nav') &&
        activeCompany?.buyer &&
        user.permissions.seedToSale.canAccessSeedToSaleIntegration
      );
    },
  },
  {
    id: 'payments',
    label: 'layout.sideNav.payments',
    icon: 'money',
    href: ({ user }) => user.featureFlags.financialPortalUrl,
    visible: ({ activeCompany, user }) => {
      return !!(
        activeCompany?.seller &&
        activeCompany?.state === 'CO' &&
        user.featureFlags.enablePaymentPortalNavItem &&
        !activeCompany?.isLogisticsOnly
      );
    },
    active: () => false,
  },
  {
    id: 'fulfillment',
    label: 'layout.sideNav.fulfillment',
    icon: 'queue',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.FULFILLMENT_QUEUE,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) =>
      !!(
        activeCompany?.seller &&
        userCompany?.staffPermissions.canManageFulfillment &&
        !activeCompany?.isLogisticsOnly
      ),
    active: ({ currentRoute }) =>
      pathStartsWith({
        path: currentRoute.path,
        value: 'fulfillment',
        prependSlug: true,
      }),
  },
  {
    id: 'deliveries',
    label: 'layout.sideNav.deliveries',
    icon: 'truck',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.LOGISTICS_DELIVERIES,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) => {
      return !!(
        activeCompany?.buyer &&
        activeCompany?.enableDeliveries &&
        userCompany?.staffPermissions.canAccessDeliveries &&
        !activeCompany?.isLogisticsOnly
      );
    },
  },
  {
    id: 'shipping',
    label: 'layout.sideNav.shipping',
    icon: 'truck',
    route: ({ activeCompany }) => ({
      name: activeCompany?.isLogisticsOnly ? ROUTE_NAMES.TRACK_ORDERS : ROUTE_NAMES.LOGISTICS,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) => {
      return !!(
        activeCompany?.isLogisticsSplashVisible ||
        (userCompany?.staffPermissions.canAccessLogistics &&
          (userCompany?.isLogisticsVisible || activeCompany?.isLogisticsOnly))
      );
    },
  },
  {
    id: 'ship-assist',
    label: 'layout.sideNav.shipAssist',
    icon: 'dolly',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.SHIP_ASSIST,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany, user }) => {
      return !!(
        activeCompany?.enablePackingApp &&
        userCompany?.staffPermissions.canAccessPackingApp &&
        user.featureFlags.enableShipAssist &&
        !activeCompany?.isLogisticsOnly
      );
    },
  },
  {
    id: 'replenishment',
    label: 'layout.sideNav.warehouse',
    icon: 'warehouse',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.REPLENISHMENT_INVENTORY,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) => {
      return !!(
        activeCompany?.group &&
        activeCompany?.group.enableReplenishment &&
        userCompany?.staffPermissions.canAccessReplenishment &&
        !activeCompany?.isLogisticsOnly
      );
    },
    active: ({ currentRoute }) =>
      pathStartsWith({
        path: currentRoute.path,
        value: 'replenishment',
      }) && !currentRoute.path.includes('catalog'),
  },
  {
    id: 'warehouse-fulfillment',
    label: 'layout.sideNav.warehouse',
    icon: 'warehouse',
    route: ({ activeCompany }) => ({
      name: getWarehouseRouteNameFromRedirects(),
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) => {
      // TODO: Refactor using activeCompanyPermissions getter (here and above)
      // This will enable using constants from constants/permissions
      return !!(
        (userCompany?.staffPermissions.canAccessBrandFulfillment ||
          userCompany?.staffPermissions.canAccessWhsFulfillment ||
          userCompany?.staffPermissions.canAccessR11TFulfillment ||
          userCompany?.staffPermissions.canManageWhsAsnCreation) &&
        !activeCompany?.isLogisticsOnly
      );
    },
    active: ({ currentRoute }) =>
      pathStartsWith({
        path: currentRoute.path,
        value: 'warehouse',
        prependSlug: true,
      }),
  },
  {
    id: 'order-deliveries',
    label: 'layout.sideNav.orderDeliveries',
    icon: 'truck',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.DELIVERIES,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany }) => !!(activeCompany?.isDeliveryProvider && !activeCompany?.isLogisticsOnly),
  },
  {
    id: 'sales-dashboard',
    label: 'layout.sideNav.salesDashboard',
    icon: 'lightbulb',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.SALES_DASHBOARD,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany, userCompany }) =>
      !!(
        activeCompany?.seller &&
        userCompany?.staffPermissions.canAccessSalesReports &&
        !activeCompany?.isLogisticsOnly
      ),
  },
  {
    id: 'buyer-reports',
    label: ({ activeCompany }) =>
      activeCompany?.seller && activeCompany?.buyer ? 'layout.sideNav.buyerReports' : 'layout.sideNav.reports',
    icon: 'lightbulb',
    route: ({ activeCompany }) => ({
      name: ROUTE_NAMES.BUYER_REPORTS,
      params: { company: activeCompany?.slug },
    }),
    visible: ({ activeCompany }) =>
      !!(activeCompany?.buyer && !activeCompany?.isBrandOwner && !activeCompany?.isLogisticsOnly),
    active: ({ currentRoute }) =>
      currentRoute.name === ROUTE_NAMES.BUYER_REPORTS ||
      pathStartsWith({
        path: currentRoute.path,
        value: 'reports',
        prependSlug: true,
      }),
  },
  // settings for admin
  {
    id: 'admin-settings',
    label: 'layout.sideNav.settings',
    icon: 'gear',
    route: ({ activeCompany }) => {
      if (activeCompany?.seller) {
        // due to work being done for LOGX-113 before updating other routes/tabs for logistics only companies,
        // we will temporarily default to routing to the payments settings page until LOGX-111 is completed.
        return {
          name: activeCompany?.isLogisticsOnly ? ROUTE_NAMES.PAYMENTS_SETTINGS : ROUTE_NAMES.INFO,
          params: { company: activeCompany?.slug },
        };
      } else if (activeCompany?.buyer) {
        return {
          name: ROUTE_NAMES.BUYER_SETTINGS_ACCOUNT_DETAILS,
          params: { company: activeCompany?.slug },
        };
      } else if (activeCompany?.isDeliveryProvider) {
        return {
          name: ROUTE_NAMES.DELIVERY_PROVIDER_SETTINGS,
          params: { company: activeCompany?.slug },
        };
      } else if (activeCompany?.isBrandOwner) {
        return {
          name: ROUTE_NAMES.BRAND_OWNER_SETTINGS,
          params: { company: activeCompany?.slug },
        };
      }

      return '/';
    },
    visible: ({ userCompany }) => {
      return !!userCompany?.staffIsAdmin;
    },
    active: ({ currentRoute }) =>
      pathStartsWith({
        path: currentRoute.path,
        value: 'settings',
        prependSlug: true,
      }),
    warning: ({ activeCompany }) => !!activeCompany?.hasWarnings,
  },
  ({ activeCompany, currentRoute, userCompany }) => {
    const hasManageLicensesAccess = canStaffAccessManageLicenses();
    const hasDeveloperSettingsAccess =
      userCompany?.staffPermissions.canAccessApi && (activeCompany?.webhooksEnabled || activeCompany?.apiEnabled);

    const visible = !!(
      !userCompany?.staffIsAdmin &&
      (hasManageLicensesAccess || hasDeveloperSettingsAccess) &&
      !activeCompany?.isLogisticsOnly
    );
    const active = pathStartsWith({
      path: currentRoute.path,
      value: 'settings',
      prependSlug: true,
    });
    let route = {};

    if (hasDeveloperSettingsAccess) {
      route = {
        name: ROUTE_NAMES.DEVELOPER_SETTINGS,
        params: { company: activeCompany?.slug },
      };
    }
    if (hasManageLicensesAccess) {
      route = {
        name: ROUTE_NAMES.LICENSES,
        params: { company: activeCompany?.slug },
      };
    }

    return {
      id: 'non-admin-settings',
      label: 'layout.sideNav.settings',
      icon: 'gear',
      route,
      visible,
      active,
    };
  },
];

export default navConfig;
